<template>
    <v-container>
        <h2 class="mb-4">{{ mode === 'create' ? 'Create Player' : mode === 'edit' ? 'Edit Player' : 'View Player' }}</h2>
        <div v-if="mode && !['edit', 'create'].includes(mode)" class="edit-button-wrapper">
            <v-btn class="mx-2" dark medium color="primary" :loading="navigating" :disabled="navigating || submitting || deletingImage" @click="onEdit">
                <v-icon dark>mdi-account-edit</v-icon>
            </v-btn>
        </div>
        <v-form :readonly="readOnly">
            <v-text-field
                v-model="player.firstName"
                :rules="rules"
                label="First Name"
                :clearable="clearable"
            />
            <v-text-field
                v-model="player.lastName"
                :rules="rules"
                label="Last Name"
                :clearable="clearable"
            />
            <v-text-field
                v-model="player.position"
                label="Position/Title"
                :clearable="clearable"
            />
            <v-select
                v-model="player.role"
                :items="roleOptions"
                label="Role"
                :clearable="clearable"
            />
            <v-text-field
                v-model="player.number"
                type="number"
                label="Number"
                :clearable="clearable"
            />
            <v-text-field
                v-model="player.height"
                label="Height"
                :clearable="clearable"
            />
            <v-menu
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
            >
                <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                        :value="formattedBirthDate"
                        label="Birthdate"
                        prepend-icon="mdi-calendar"
                        :clearable="clearable"
                        :readonly="true"
                        v-bind="attrs"
                        v-on="on"
                        @click:clear="player.birthDate = ''"
                    />
                </template>
                <v-date-picker
                    v-model="player.birthDate"
                    @input="$event => player.birthDate = $event"
                />
            </v-menu>
            <v-text-field
                v-model="player.nationality"
                label="Nationality"
                :clearable="clearable"
            />
            <v-select
                v-model="player.sport"
                :items="sports"
                item-text="title"
                item-value="id"
                return-object
                label="Sport"
                :clearable="clearable"
            />
            <v-file-input
                v-if="mode !== 'show'"
                v-model="newImage"
                label="Player Image"
                :rules="imageRules"
                filled
                show-size
                prepend-icon="mdi-camera"
                @change="createNewImagePreview"
            />
            <div v-if="previewImage && !previewImage.shouldDelete" class="image-input" :class="{'green-border': !previewImage.id}">
                <div v-if="mode === 'edit'" class="delete-btn">
                    <v-btn class="mx-2 white--text" small color="secondary" :loading="deletingImage" :disabled="deletingImage || submitting || navigating" @click="onImageDelete">
                        X
                    </v-btn>
                </div>
                <v-img v-if="previewImage.url" class="img-preview" :src="previewImage.url" contain height="180" />
            </div>
        </v-form>
        <div v-if="mode !== 'show'" class="submit-button-wrapper">
            <v-btn class="mx-2 white--text" :loading="submitting" :disabled="shouldDisableSubmit() || submitting || navigating || deletingImage" large color="primary" @click="onSubmit">
                Submit
            </v-btn>
        </div>
    </v-container>
</template>

<script>
    import imageService from '@/services/image-service'
    import playerService from '@/services/player-service'
    import sportService from '@/services/sport-service'

    export default {
        name: 'PlayerForm',
        data: () => {
            return {
                mode: null,
                readOnly: false,
                clearable: true,
                apiUrl: process.env.VUE_APP_API_HOST_DOMAIN,
                player: {
                    firstName: '',
                    lastName: '',
                    position: '',
                    role: null,
                    number: null,
                    height: '',
                    birthDate: '',
                    nationality: '',
                    sport: null
                },
                playerId: null,
                sports: [],
                roleOptions: ['staff', 'player'],
                rules: [
                    v => !!v || 'Field is required'
                ],
                newImage: null,
                previewImage: null,
                imageRules: [
                    value => !value || value.size < 2000000 || 'Image size should be less than 2 MB!'
                ],
                submitting: false,
                navigating: false,
                deletingImage: false
            }
        },
        created() {
            this.mode = this.$route.params.mode
            this.loadSports()
            if (this.mode === 'create') {
                return
            } else if (this.mode === 'show') {
                this.readOnly = true
                this.clearable = false
            }
            this.playerId = this.$route.params.playerId
            if (this.playerId) {
                playerService.getPlayerById(this.playerId).then(resp => {
                    this.player = {
                        ...this.player,
                        ...resp.data.player
                    }
                    this.player.birthDate = this.normalizeDateValue(this.player.birthDate)
                    this.previewImage = this.player.image
                    if (this.previewImage) {
                        this.previewImage.url = process.env.VUE_APP_GOOGLE_BUCKET_URL + this.previewImage.imageName
                    }
                })
            }
        },
        computed: {
            formattedBirthDate() {
                return this.formatDateDisplay(this.player.birthDate)
            }
        },
        methods: {
            loadSports() {
                sportService.getAllSports().then(resp => {
                    this.sports = resp.data.data || []
                })
            },
            formatDateDisplay(value) {
                if (!value) {
                    return ''
                }
                const normalized = String(value).slice(0, 10)
                const [year, month, day] = normalized.split('-')
                if (!year || !month || !day) {
                    return normalized
                }
                return `${day}/${month}/${year}`
            },
            shouldDisableSubmit() {
                return !this.player.firstName || !this.player.lastName
            },
            async onEdit() {
                this.navigating = true
                try {
                    await this.$router.push(`/players/${this.playerId}/edit`)
                } finally {
                    this.navigating = false
                }
            },
            async createPlayer() {
                const response = await playerService.createPlayer(this.buildPlayerFormData())
                if (response.data.id) {
                    this.playerId = response.data.id
                }
                this.$router.push(`/players`)
            },
            async editPlayer() {
                await playerService.editPlayerById(this.playerId, this.buildPlayerFormData())
                this.$router.push(`/players`)
            },
            async onSubmit() {
                this.submitting = true
                try {
                    if (this.mode === 'edit') {
                        await this.editPlayer()
                    } else if (this.mode === 'create') {
                        await this.createPlayer()
                    }
                    await this.deletePlayerImage()
                } finally {
                    this.submitting = false
                }
            },
            async deletePlayerImage() {
                if (this.previewImage && this.previewImage.id && this.previewImage.shouldDelete) {
                    await imageService.deleteImageById(this.previewImage.id)
                    this.previewImage = null
                }
            },
            createNewImagePreview() {
                if (!this.hasSelectedImage(this.newImage)) {
                    this.previewImage = null
                    return
                }
                this.previewImage = this.newImage
                this.previewImage.url = URL.createObjectURL(this.newImage)
            },
            normalizeDateValue(value) {
                if (!value) {
                    return ''
                }
                return String(value).slice(0, 10)
            },
            buildPlayerFormData() {
                const formData = new FormData()

                formData.append('firstName', this.player.firstName || '')
                formData.append('lastName', this.player.lastName || '')
                formData.append('position', this.player.position || '')
                formData.append('role', this.player.role || '')
                formData.append('number', this.player.number !== null && this.player.number !== undefined ? this.player.number : '')
                formData.append('height', this.player.height || '')
                formData.append('birthDate', this.player.birthDate || '')
                formData.append('nationality', this.player.nationality || '')
                formData.append('sportId', this.player.sport ? this.player.sport.id : '')

                if (this.hasSelectedImage(this.newImage)) {
                    formData.append('file', this.newImage)
                }

                return formData
            },
            onImageDelete() {
                this.deletingImage = true
                if (this.previewImage && this.previewImage.id) {
                    this.previewImage.shouldDelete = true
                    this.previewImage.url = null
                    this.previewImage = { ...this.previewImage }
                } else {
                    this.newImage = null
                }
                this.deletingImage = false
            },
            hasSelectedImage(image) {
                return image && typeof image === 'object' && image.size !== undefined && image.name
            }
        }
    }
</script>

<style scoped>
.edit-button-wrapper {
    width: 100%;
    display: flex;
    justify-content: flex-end;
}

.submit-button-wrapper {
    width: 100%;
    display: flex;
    justify-content: center;
    margin-top: 2vh;
}

.image-input {
    border: 2px dashed #ccc;
    padding: 20px;
    border-radius: 4px;
    margin-top: 20px;
    position: relative;
}

.image-input.green-border {
    border-color: #4caf50;
}

.delete-btn {
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 10;
}

.img-preview {
    width: 100%;
    max-width: 100%;
    height: 180px;
    margin: 0 auto 10px;
}
</style>


